import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { HubDataTable } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { megaventoryAdjustmentDocumentUrl } from "@/lib/megaventoryDocumentUrls";
import { buildMrpSkuDetailPath } from "@/lib/mrp";

import { fetchAdjustmentDetail, type AdjustmentDetailLine } from "./adjustmentDetailApi";

function formatWhen(value: string | null): string {
  if (value == null || value.trim() === "") {
    return "";
  }
  return value.slice(0, 10);
}

export function AdjustmentDetailPage(): JSX.Element {
  const { id: pathId } = useParams();
  const adjustmentId = Number.parseInt(pathId ?? "", 10);
  const selectedId = Number.isFinite(adjustmentId) && adjustmentId > 0 ? adjustmentId : null;

  const detailQuery = useQuery({
    queryKey: ["mrp", "adjustment", selectedId],
    queryFn: () => fetchAdjustmentDetail(selectedId!),
    enabled: selectedId != null,
  });

  const detail = detailQuery.data;
  const header = detail?.adjustment;
  const externalHref =
    header?.mv_id != null ? megaventoryAdjustmentDocumentUrl(header.mv_id, header.mv_number) : null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {header != null ? `${header.kind_label} ${header.display_number}` : "Adjustment"}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {selectedId == null ? (
            <p>This adjustment link is not valid.</p>
          ) : detailQuery.isPending ? (
            <p>Loading adjustment.</p>
          ) : detailQuery.isError || header == null ? (
            <p>Adjustment not found.</p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone="neutral">{header.kind_label}</Badge>
                {header.status != null && header.status !== "" ? (
                  <Badge tone="success">{header.status}</Badge>
                ) : null}
                <span>{formatWhen(header.occurred_at)}</span>
                <span>{header.location_abbreviation ?? header.location_name ?? ""}</span>
                {externalHref != null ? (
                  <a href={externalHref} target="_blank" rel="noreferrer">
                    Open in Megaventory
                  </a>
                ) : null}
              </div>
              {header.comment != null && header.comment.trim() !== "" ? <p>{header.comment}</p> : null}
            </div>
          )}
        </CardBody>
      </Card>
      {header != null ? (
        <HubDataTable<AdjustmentDetailLine>
          fillViewport={false}
          rows={detail?.lines ?? []}
          rowKey={(line) => line.id}
          columns={[
            {
              id: "sku",
              header: "SKU",
              sortValue: (line) => line.sku,
              cell: (line) =>
                line.sku !== "" ? (
                  <Link className="text-blue-700 hover:underline" to={buildMrpSkuDetailPath(line.sku)}>
                    {line.sku}
                  </Link>
                ) : (
                  ""
                ),
            },
            {
              id: "quantity",
              header: "Quantity",
              align: "right",
              sortValue: (line) => line.quantity,
              cell: (line) => line.quantity,
            },
            {
              id: "price",
              header: "Price",
              align: "right",
              sortValue: (line) => line.price ?? "",
              cell: (line) => (line.price == null ? "" : line.price),
            },
          ]}
        />
      ) : null}
    </div>
  );
}
